import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Priority } from '@app/shared/constants/global.enum';
import { Subject, takeUntil } from 'rxjs';
import { TeamManagementService } from '../team-management.service';
import {
  TeamLeaderTimesheetCounts,
  TeamTimesheetMetricsService,
  TeamTimesheetStatusCounts,
} from './team-timesheet-metrics.service';

interface MonthOption {
  label: string;
  value: number;
  year: number;
  selected?: boolean;
}

interface MetricTile {
  key: keyof Pick<
    TeamTimesheetStatusCounts,
    'pendingSubmission' | 'pendingApproval' | 'approved' | 'rejected'
  >;
  label: string;
  value: number;
  tone: 'warning' | 'neutral' | 'success' | 'danger';
}

const STATUS_COLORS = {
  pendingSubmission: '#d97706',
  pendingApproval: '#2563eb',
  approved: '#003f2d',
  rejected: '#dc2626',
};

@Component({
  selector: 'app-team-timesheet-metrics',
  templateUrl: './team-timesheet-metrics.component.html',
  styleUrls: ['./team-timesheet-metrics.component.scss'],
})
export class TeamTimesheetMetricsComponent implements OnInit, OnDestroy {
  loading = false;
  loadError = false;
  monthOptions: MonthOption[] = [];
  selectedMonth = 0;
  selectedYear = 0;

  /** Label of the currently-selected month option — kept in sync with
   *  selectedMonth/selectedYear so the pill <select> can bind to it via
   *  ngModel and stay visually consistent with the perf-summary year picker. */
  get selectedMonthKey(): string {
    const opt = this.monthOptions.find(
      (o) => o.value === this.selectedMonth && o.year === this.selectedYear,
    );
    return opt?.label ?? '';
  }
  totalContractors = 0;
  approvalRate = 100;
  chartData: any = null;
  chartOptions: any = null;
  tiles: MetricTile[] = [
    { key: 'pendingSubmission', label: 'Pending Submission', value: 0, tone: 'warning' },
    { key: 'pendingApproval', label: 'Pending Approval', value: 0, tone: 'neutral' },
    { key: 'approved', label: 'Approved', value: 0, tone: 'success' },
    { key: 'rejected', label: 'Rejected', value: 0, tone: 'danger' },
  ];

  private currentEmployeeId: string | null = null;
  private currentFilterType: string | undefined = undefined;
  private readonly destroy$ = new Subject<void>();
  priority = Priority;

  constructor(
    private readonly teamService: TeamManagementService,
    private readonly metricsService: TeamTimesheetMetricsService,
    private readonly router: Router,
  ) {}

  navigateToTimesheet(): void {
    this.router.navigate(['/vendor-management'], {
      state: { month: this.selectedMonth, year: this.selectedYear },
    });
  }

  ngOnInit(): void {
    this.generateLast12Months();
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    this.selectedMonth = prev.getMonth() + 1;
    this.selectedYear = prev.getFullYear();
    this.chartOptions = this.buildChartOptions();

    this.teamService
      .getSelectedDropdownValue()
      .pipe(takeUntil(this.destroy$))
      .subscribe((selection) => {
        if (!selection?.employeeId) return;
        this.currentEmployeeId = selection.employeeId;
        this.currentFilterType = selection.filterType;
        this.fetchData();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onMonthChange(event: any): void {
    const label = typeof event === 'string' ? event : event?.value;
    const option = this.monthOptions.find((o) => o.label === label || o.value === label);
    if (!option) return;
    this.selectedMonth = option.value;
    this.selectedYear = option.year;
    this.fetchData();
  }

  private generateLast12Months(): void {
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      this.monthOptions.push({
        label: date.toLocaleString('default', { month: 'long', year: 'numeric' }),
        value: date.getMonth() + 1,
        year: date.getFullYear(),
        selected: i === 1,
      });
    }
  }

  private fetchData(): void {
    if (!this.currentEmployeeId) return;
    this.loading = true;
    this.loadError = false;

    this.metricsService
      .getStatusCountsForEmployee(this.currentEmployeeId, this.currentFilterType, this.selectedMonth, this.selectedYear)
      .subscribe({
        next: (counts) => this.applyCounts(counts),
        error: () => {
          this.loadError = true;
          this.loading = false;
        },
      });

    this.metricsService
      .getStatusCountsByLeader(this.currentEmployeeId, this.currentFilterType, this.selectedMonth, this.selectedYear)
      .subscribe({
        next: (rows) => this.renderChart(rows ?? []),
        error: () => {
          this.loadError = true;
          this.loading = false;
        },
      });
  }

  private applyCounts(counts: TeamTimesheetStatusCounts): void {
    this.tiles = this.tiles.map((tile) => ({ ...tile, value: counts[tile.key] ?? 0 }));
    this.totalContractors = counts.total ?? 0;
    const inFlow = counts.pendingApproval + counts.approved + counts.rejected;
    const actioned = counts.approved + counts.rejected;
    this.approvalRate = inFlow > 0 ? Math.round((actioned / inFlow) * 100) : 100;
    this.loading = false;
  }

  private renderChart(rows: TeamLeaderTimesheetCounts[]): void {
    const fullNames = rows.map((d) => d.leaderName);
    const labels = fullNames.map((n) => (n.length > 14 ? n.substring(0, 13).trimEnd() + '…' : n));
    this.chartData = {
      labels,
      fullLabels: fullNames,
      datasets: [
        { label: 'Pending Submission', backgroundColor: STATUS_COLORS.pendingSubmission, data: rows.map((d) => d.pendingSubmission), stack: 'status', borderRadius: 4, borderSkipped: false, maxBarThickness: 56 },
        { label: 'Pending Approval', backgroundColor: STATUS_COLORS.pendingApproval, data: rows.map((d) => d.pendingApproval), stack: 'status', borderRadius: 4, borderSkipped: false, maxBarThickness: 56 },
        { label: 'Approved', backgroundColor: STATUS_COLORS.approved, data: rows.map((d) => d.approved), stack: 'status', borderRadius: 4, borderSkipped: false, maxBarThickness: 56 },
        { label: 'Rejected', backgroundColor: STATUS_COLORS.rejected, data: rows.map((d) => d.rejected), stack: 'status', borderRadius: 4, borderSkipped: false, maxBarThickness: 56 },
      ],
    };
  }

  private buildChartOptions(): any {
    return {
      indexAxis: 'x',
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 2, bottom: 0 } },
      plugins: {
        legend: { position: 'bottom', labels: { boxWidth: 8, boxHeight: 8, font: { size: 10 }, padding: 8 } },
        datalabels: { display: false },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (items: any[]) => {
              const idx = items[0]?.dataIndex;
              return items[0]?.chart?.data?.fullLabels?.[idx] || items[0]?.label;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { autoSkip: false, maxRotation: 20, minRotation: 0, font: { size: 11 }, color: '#6b7280', padding: 6 },
          categoryPercentage: 0.6,
          barPercentage: 0.85,
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: '#f3f4f6', drawBorder: false },
          ticks: { precision: 0, font: { size: 11 }, color: '#6b7280' },
          afterFit: (scale: any) => { scale.width = 36; },
        },
      },
    };
  }
}
